import Phaser from 'phaser';
import { GAME_WIDTH } from '../config';
import {
  MAX_DICE_SLOTS,
  STARTING_UNLOCKED_DICE_SLOTS,
  computeCharacterCombatStats,
  createSeededRng,
  startDungeonRun,
  buildEquipmentContextFromLoadout,
  createEquipmentSnapshot,
} from '../../domain';
import {
  grantGeneralLevels,
  grantGeneralTalentPoints,
  grantPointsToOpenSpecialisations,
  grantVanguardLevels,
  grantVanguardTalentPoints,
  loadDevPermanent,
  saveDevPermanent,
  snapshotDevProgress,
} from '../../persistence/debugProgress';
import {
  buildSavedGame,
  createSaveStore,
} from '../../persistence/saveStore';
import {
  grantDevDualWieldKitToPermanent,
  grantDevEquipmentToPermanent,
} from '../../persistence/equipmentFixtures';
import {
  createDefaultPermanentSave,
  inventoryFromPermanent,
} from '../../persistence/schema';
import {
  addPrimaryButton,
  addSecondaryButton,
} from '../ui/hubUi';
import {
  addDungeonProgressionDevControls,
  type DungeonProgressionUiState,
} from '../ui/dungeonProgressionDevControls';
import { mountShell, paintShellBackdrop } from '../ui/shellUi';

/**
 * Settings shell tab — player-facing placeholders + clearly separated DEV section
 * (starting dice, practice, clear save, talent points / level grants).
 */
export class SettingsScene extends Phaser.Scene {
  private startingDice = STARTING_UNLOCKED_DICE_SLOTS;
  private startingDiceLabel?: Phaser.GameObjects.Text;
  private progressLabel?: Phaser.GameObjects.Text;
  private scrollRoot?: Phaser.GameObjects.Container;
  private scrollMin = 0;
  private dragStartY = 0;
  private scrollStartY = 0;
  private dragPointerId: number | null = null;
  private scrolling = false;
  private progression: DungeonProgressionUiState = {
    dungeonLevel: 1,
    mutation: 'standard',
  };

  constructor() {
    super('Settings');
  }

  create(): void {
    paintShellBackdrop(this, 0x0f1420);
    const bounds = mountShell(this, 'settings');
    const cx = GAME_WIDTH / 2;

    const maskRect = this.add
      .rectangle(
        GAME_WIDTH / 2,
        bounds.top + bounds.height / 2,
        GAME_WIDTH,
        bounds.height,
        0xffffff,
        0,
      )
      .setOrigin(0.5);
    const mask = maskRect.createGeometryMask();

    this.scrollRoot = this.add.container(0, bounds.top);
    this.scrollRoot.setMask(mask);

    let y = 16;
    this.scrollRoot.add(
      this.add
        .text(cx, y, 'Settings', {
          fontFamily: 'Georgia, "Times New Roman", serif',
          fontSize: '24px',
          color: '#e8d5a3',
        })
        .setOrigin(0.5),
    );
    y += 36;

    this.scrollRoot.add(
      this.add
        .text(cx, y, 'Audio / accessibility options arrive later.', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#6e7694',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 40 },
        })
        .setOrigin(0.5),
    );
    y += 40;

    // Player-facing placeholders
    for (const label of ['Sound — Coming Soon', 'Haptics — Coming Soon']) {
      const row = this.add
        .rectangle(cx, y, 280, 40, 0x1c2438)
        .setStrokeStyle(1, 0x3a4158);
      this.scrollRoot.add(row);
      this.scrollRoot.add(
        this.add
          .text(cx, y, label, {
            fontFamily: 'system-ui, sans-serif',
            fontSize: '13px',
            color: '#9aa3c0',
          })
          .setOrigin(0.5),
      );
      y += 52;
    }

    y += 12;
    // DEV section divider
    this.scrollRoot.add(
      this.add.rectangle(cx, y, GAME_WIDTH - 32, 2, 0xc9a227, 0.35),
    );
    y += 18;
    this.scrollRoot.add(
      this.add
        .text(cx, y, 'DEVELOPMENT', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '14px',
          color: '#c9a227',
          fontStyle: 'bold',
        })
        .setOrigin(0.5),
    );
    y += 22;
    this.scrollRoot.add(
      this.add
        .text(cx, y, 'Debug overrides — not part of player progression.', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '11px',
          color: '#b07a3a',
          align: 'center',
          wordWrap: { width: GAME_WIDTH - 40 },
        })
        .setOrigin(0.5),
    );
    y += 28;
    this.progressLabel = this.add
      .text(cx, y, this.progressCaption(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '11px',
        color: '#e8d5a3',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 36 },
      })
      .setOrigin(0.5);
    this.scrollRoot.add(this.progressLabel);
    y += 36;

    this.startingDice = STARTING_UNLOCKED_DICE_SLOTS;
    this.renderStartingDiceControl(cx, y);
    y += 70;

    const progButtons: { label: string; run: () => void }[] = [
      {
        label: '+5 Vanguard talent pts',
        run: () => this.applyProgress((p) => grantVanguardTalentPoints(p, 5)),
      },
      {
        label: '+1 Vanguard class level',
        run: () => this.applyProgress((p) => grantVanguardLevels(p, 1)),
      },
      {
        label: '+5 Vanguard class levels',
        run: () => this.applyProgress((p) => grantVanguardLevels(p, 5)),
      },
      {
        label: '+1 General level',
        run: () => this.applyProgress((p) => grantGeneralLevels(p, 1)),
      },
      {
        label: '+5 General talent pts',
        run: () => this.applyProgress((p) => grantGeneralTalentPoints(p, 5)),
      },
      {
        label: 'Grant pts to open specs (15)',
        run: () =>
          this.applyProgress((p) => grantPointsToOpenSpecialisations(p)),
      },
      {
        label: 'Preview loot toasts (DEV)',
        run: () => this.previewLootToasts(),
      },
      {
        label: 'Add equipment to inventory (DEV)',
        run: () => this.grantEquipmentKit(),
      },
      {
        label: 'Add + equip dual-wield kit (DEV)',
        run: () => this.grantDualWieldKit(),
      },
    ];
    for (const btn of progButtons) {
      const row = addSecondaryButton(this, cx, y, btn.label, btn.run, 280);
      this.scrollRoot.add(row);
      y += 50;
    }

    const openTalents = addPrimaryButton(
      this,
      cx,
      y,
      'Open Talents',
      () => this.scene.start('Talents'),
      240,
    );
    this.scrollRoot.add(openTalents);
    y += 56;

    const prog = addDungeonProgressionDevControls(
      this,
      cx,
      y,
      this.progression,
      { addToContainer: this.scrollRoot },
    );
    this.progression = prog.state;
    y = prog.nextY + 12;

    const startDungeon = addPrimaryButton(
      this,
      cx,
      y,
      'Start dungeon (DEV dice)',
      () => {
        const store = createSaveStore();
        const previous = store.load();
        const permanent =
          previous?.permanent ??
          createDefaultPermanentSave({
            unlockedDiceSlots: this.startingDice,
          });
        const seed = Date.now() >>> 0;
        const rng = createSeededRng(seed);
        const classRanks = permanent.classes.vanguard.talentRanks ?? {};
        const inventory = inventoryFromPermanent(permanent);
        const equipmentSnapshot = createEquipmentSnapshot(inventory);
        const equipment = buildEquipmentContextFromLoadout(
          inventory,
          classRanks,
        );
        const combatStats = computeCharacterCombatStats(
          classRanks,
          equipment,
          permanent.general.talentRanks ?? {},
          {
            classId: permanent.profile.activeClassId ?? 'vanguard',
            classLevel: permanent.classes.vanguard.level ?? 1,
          },
        );
        const dungeonRun = startDungeonRun(rng, seed, {
          unlockedDiceSlots: this.startingDice,
          combatStats,
          unlockedActiveSkillIds: combatStats.unlockedActiveSkillIds,
          playerMaxHp: combatStats.maxHp,
          equipmentSnapshot,
          dungeonLevel: this.progression.dungeonLevel,
          mutation: this.progression.mutation,
        });
        store.save(
          buildSavedGame(dungeonRun, rng.state, {
            permanent: {
              ...permanent,
              unlockedDiceSlots:
                permanent.unlockedDiceSlots === STARTING_UNLOCKED_DICE_SLOTS
                  ? this.startingDice
                  : permanent.unlockedDiceSlots,
            },
          }),
        );
        this.scene.start('Combat', {
          mode: 'dungeon',
          dungeonRun,
          rngState: rng.state,
        });
      },
      260,
    );
    this.scrollRoot.add(startDungeon);
    y += 60;

    const practice = addSecondaryButton(
      this,
      cx,
      y,
      'Practice (3 dice)',
      () => {
        this.scene.start('Combat', {
          mode: 'practice',
          unlockedDiceSlots: 3,
          seed: Date.now() >>> 0,
          enemyTemplateIds: ['slime', 'goblin', 'bat'],
        });
      },
      240,
    );
    this.scrollRoot.add(practice);
    y += 56;

    const clear = addSecondaryButton(
      this,
      cx,
      y,
      'Clear local save',
      () => {
        createSaveStore().clear();
        this.scene.restart();
      },
      240,
    );
    this.scrollRoot.add(clear);
    y += 56;

    const legacy = addSecondaryButton(
      this,
      cx,
      y,
      'Open full Debug menu',
      () => this.scene.start('DebugMenu'),
      240,
    );
    this.scrollRoot.add(legacy);
    y += 72;

    this.scrollMin = Math.min(0, bounds.height - y);
    // Keep scroll content above any chrome; never cover buttons with a drag hit.
    this.scrollRoot.setDepth(10);
    this.bindScroll(bounds);
  }

  /** DEV: persist one of each lootable base into the bag. */
  private grantEquipmentKit(): void {
    this.applyProgress((p) => grantDevEquipmentToPermanent(p).permanent);
  }

  /** DEV: persist sword+dagger and equip for Twin Strike offer tests. */
  private grantDualWieldKit(): void {
    this.applyProgress((p) => grantDevDualWieldKitToPermanent(p).permanent);
  }

  /** DEV-only toast preview for M7-B screenshots — does not mutate saves. */
  private previewLootToasts(): void {
    const samples = [
      { name: 'Iron Sword (rare)', color: '#5b9fff' },
      { name: 'Copper Ring (uncommon)', color: '#5ecf7a' },
      { name: 'Ember Special Die (epic)', color: '#c07bff' },
    ];
    samples.forEach((sample, i) => {
      const y = 80 + i * 36;
      const bg = this.add
        .rectangle(GAME_WIDTH / 2, y, GAME_WIDTH - 24, 32, 0x121820, 0.92)
        .setStrokeStyle(1, 0x3a4558)
        .setDepth(2000);
      const label = this.add
        .text(24, y, `Loot · ${sample.name}`, {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: sample.color,
        })
        .setOrigin(0, 0.5)
        .setDepth(2001);
      this.tweens.add({
        targets: [bg, label],
        alpha: 0,
        delay: 2200 + i * 150,
        duration: 400,
        onComplete: () => {
          bg.destroy();
          label.destroy();
        },
      });
    });
  }

  /**
   * Scene-level scroll — do NOT lay an interactive rect over the list
   * (that steals taps from DEV / kit buttons). Only pan after a drag threshold
   * so short taps still fire button pointerup handlers.
   */
  private bindScroll(bounds: { top: number; height: number }): void {
    const top = bounds.top;
    const bottom = bounds.top + bounds.height;

    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y < top || pointer.y > bottom) {
        return;
      }
      this.dragPointerId = pointer.id;
      this.dragStartY = pointer.y;
      this.scrollStartY = this.scrollRoot?.y ?? top;
      this.scrolling = false;
    });

    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (
        !pointer.isDown ||
        !this.scrollRoot ||
        this.dragPointerId !== pointer.id
      ) {
        return;
      }
      const dy = pointer.y - this.dragStartY;
      if (!this.scrolling && Math.abs(dy) < 12) {
        return;
      }
      this.scrolling = true;
      this.scrollRoot.y = Phaser.Math.Clamp(
        this.scrollStartY + dy,
        top + this.scrollMin,
        top,
      );
    });

    this.input.on('pointerup', () => {
      this.dragPointerId = null;
      this.scrolling = false;
    });
  }

  private renderStartingDiceControl(cx: number, y: number): void {
    this.scrollRoot!.add(
      this.add
        .text(cx, y - 22, 'Starting dice (not permanent progression)', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '12px',
          color: '#b07a3a',
        })
        .setOrigin(0.5),
    );

    const minus = this.add
      .rectangle(cx - 70, y + 10, 48, 40, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    this.scrollRoot!.add(minus);
    this.scrollRoot!.add(
      this.add
        .text(cx - 70, y + 10, '−', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          color: '#e8d5a3',
        })
        .setOrigin(0.5),
    );

    this.startingDiceLabel = this.add
      .text(cx, y + 10, this.startingDiceCaption(), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '15px',
        color: '#f2e6c8',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.scrollRoot!.add(this.startingDiceLabel);

    const plus = this.add
      .rectangle(cx + 70, y + 10, 48, 40, 0x2a3350)
      .setStrokeStyle(1, 0xc9a227)
      .setInteractive({ useHandCursor: true });
    this.scrollRoot!.add(plus);
    this.scrollRoot!.add(
      this.add
        .text(cx + 70, y + 10, '+', {
          fontFamily: 'system-ui, sans-serif',
          fontSize: '22px',
          color: '#e8d5a3',
        })
        .setOrigin(0.5),
    );

    minus.on('pointerup', () => this.adjustStartingDice(-1));
    plus.on('pointerup', () => this.adjustStartingDice(1));
  }

  private startingDiceCaption(): string {
    const n = this.startingDice;
    const def = n === STARTING_UNLOCKED_DICE_SLOTS ? ' · default' : '';
    return `${n} die${n === 1 ? '' : 's'}${def}`;
  }

  private adjustStartingDice(delta: number): void {
    this.startingDice = Math.min(
      MAX_DICE_SLOTS,
      Math.max(1, this.startingDice + delta),
    );
    this.startingDiceLabel?.setText(this.startingDiceCaption());
  }

  private progressCaption(): string {
    const { permanent } = loadDevPermanent();
    const s = snapshotDevProgress(permanent);
    return `Gen Lv ${s.generalLevel} (${s.generalUnspentPoints} pts) · Vanguard Lv ${s.vanguardLevel} (${s.unspentPoints} pts)`;
  }

  private applyProgress(
    mutator: (permanent: ReturnType<typeof loadDevPermanent>['permanent']) =>
      ReturnType<typeof loadDevPermanent>['permanent'],
  ): void {
    const { permanent, previous } = loadDevPermanent();
    saveDevPermanent(mutator(permanent), previous);
    // Restart so shell HUD + progress readout both refresh from save.
    this.scene.restart();
  }
}
